class StoreImageSearchError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'StoreImageSearchError';
    this.code = Number(code);
  }
}

class StoreImageSearch extends HTMLElement {
  constructor() {
    super();
    this.file = null;
    this.previewUrl = null;
    this.abortController = null;
    this.currentPage = 1;
    this.pageSize = 20;
    this.isLoading = false;
  }

  connectedCallback() {
    this.root = this.closest('.template-search');
    this.endpoint = this.dataset.endpoint || '/apps/bestdealer/store-image-search';
    this.pageSize = Math.min(100, Math.max(1, Number(this.dataset.pageSize) || 20));

    this.form = this.querySelector('[data-image-search-form]');
    this.fileInput = this.querySelector('[data-file-input]');
    this.dropzone = this.querySelector('[data-dropzone]');
    this.dropzoneContent = this.querySelector('[data-dropzone-content]');
    this.preview = this.querySelector('[data-preview]');
    this.previewImage = this.querySelector('[data-preview-image]');
    this.fileName = this.querySelector('[data-file-name]');
    this.fileSize = this.querySelector('[data-file-size]');
    this.fileError = this.querySelector('[data-file-error]');
    this.searchButton = this.querySelector('[data-search-button]');
    this.searchButtonLabel = this.querySelector('[data-search-button-label]');
    this.buttonSpinner = this.querySelector('[data-button-spinner]');
    this.resetButton = this.querySelector('[data-reset-button]');
    this.loadingState = this.querySelector('[data-loading-state]');
    this.errorState = this.querySelector('[data-error-state]');
    this.errorMessage = this.querySelector('[data-error-message]');
    this.emptyState = this.querySelector('[data-empty-state]');
    this.openSourcingButton = this.querySelector('[data-open-sourcing-button]');
    this.resultsSection = this.querySelector('[data-results-section]');
    this.resultsHeading = this.querySelector('[data-results-heading]');
    this.resultsList = this.querySelector('[data-results-list]');
    this.pagination = this.querySelector('[data-pagination]');
    this.previousPageButton = this.querySelector('[data-previous-page]');
    this.nextPageButton = this.querySelector('[data-next-page]');
    this.pageStatus = this.querySelector('[data-page-status]');
    this.liveRegion = this.querySelector('[data-live-region]');

    this.bindEvents();
  }

  disconnectedCallback() {
    this.abortPendingRequest();
    this.revokePreviewUrl();
  }

  bindEvents() {
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.search(1);
    });

    this.fileInput.addEventListener('change', () => {
      const files = Array.from(this.fileInput.files || []);
      this.selectFiles(files);
    });

    ['dragenter', 'dragover'].forEach((eventName) => {
      this.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.add('is-dragging');
      });
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      this.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.remove('is-dragging');
      });
    });

    this.dropzone.addEventListener('drop', (event) => {
      const files = Array.from(event.dataTransfer?.files || []);
      this.selectFiles(files);
    });

    this.resetButton.addEventListener('click', () => this.reset());
    this.querySelector('[data-empty-reset-button]').addEventListener('click', () => this.reset());
    this.openSourcingButton?.addEventListener('click', () => this.openSourcing());
    this.querySelector('[data-retry-button]').addEventListener('click', () => this.search(this.currentPage));
    this.previousPageButton.addEventListener('click', () => this.search(this.currentPage - 1));
    this.nextPageButton.addEventListener('click', () => this.search(this.currentPage + 1));

    document.addEventListener('store-image-search:unavailable', () => {
      document.querySelectorAll('[data-image-search-entry]').forEach((entry) => {
        entry.hidden = true;
      });
    });
  }

  selectFiles(files) {
    this.clearFileError();

    if (files.length !== 1) {
      this.showFileError('Please choose one JPEG, PNG, or WebP image.');
      this.fileInput.value = '';
      return;
    }

    const file = files[0];
    const validationError = this.validateFile(file);
    if (validationError) {
      this.showFileError(validationError);
      this.fileInput.value = '';
      return;
    }

    this.abortPendingRequest();
    this.clearResponseStates();
    this.file = file;
    this.currentPage = 1;
    this.revokePreviewUrl();
    this.previewUrl = URL.createObjectURL(file);
    this.previewImage.src = this.previewUrl;
    this.fileName.textContent = file.name || 'Selected image';
    this.fileSize.textContent = this.formatFileSize(file.size);
    this.dropzoneContent.hidden = true;
    this.preview.hidden = false;
    this.searchButton.disabled = false;
    this.resetButton.hidden = false;
    this.announce(`${this.fileName.textContent} selected. Ready to search.`);
  }

  validateFile(file) {
    if (!file || file.size === 0) return 'Please choose a valid image and try again.';
    if (file.size > 10 * 1024 * 1024) return 'This image is larger than 10 MB. Please choose a smaller file.';

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const allowedExtension = /\.(jpe?g|png|webp)$/i.test(file.name || '');
    if ((file.type && !allowedTypes.includes(file.type)) || (!file.type && !allowedExtension)) {
      return 'Please choose a JPEG, PNG, or WebP image.';
    }

    return null;
  }

  async search(page) {
    if (!this.file || this.isLoading || page < 1) return;

    this.abortPendingRequest();
    const requestController = new AbortController();
    this.abortController = requestController;
    this.currentPage = page;
    this.clearResponseStates();
    this.setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', this.file, this.file.name);

      const requestUrl = new URL(this.endpoint, window.location.origin);
      if (requestUrl.origin !== window.location.origin) {
        throw new StoreImageSearchError(500, 'Image search must use the store App Proxy.');
      }
      requestUrl.searchParams.set('page', String(page));
      requestUrl.searchParams.set('pageSize', String(this.pageSize));

      const response = await fetch(requestUrl.toString(), {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: requestController.signal,
      });

      const payload = await this.parseJsonResponse(response);
      const code = Number(payload?.code);
      if (code !== 0) throw new StoreImageSearchError(code, payload?.message);
      if (!response.ok) throw new StoreImageSearchError(response.status, payload?.message);

      this.renderResponse(payload?.data || {});
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderError(error);
    } finally {
      if (this.abortController === requestController) {
        this.abortController = null;
        this.setLoading(false);
      }
    }
  }

  async parseJsonResponse(response) {
    try {
      return await response.json();
    } catch (_error) {
      throw new StoreImageSearchError(response.status || 500, 'Invalid server response.');
    }
  }

  renderResponse(data) {
    const items = Array.isArray(data.items) ? [...data.items] : [];
    items.sort((first, second) => Number(second.score || 0) - Number(first.score || 0));

    const total = Math.max(0, Number(data.total) || items.length);
    const page = Math.max(1, Number(data.page) || this.currentPage);
    const pageSize = Math.max(1, Number(data.pageSize) || this.pageSize);
    this.currentPage = page;

    if (items.length === 0) {
      this.emptyState.hidden = false;
      this.announce('No close product matches were found.');
      return;
    }

    this.resultsList.replaceChildren(...items.map((item) => this.createProductCard(item)));
    this.resultsHeading.textContent = `${total} ${total === 1 ? 'product' : 'products'} found`;
    this.resultsSection.hidden = false;

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    this.pagination.hidden = totalPages <= 1;
    this.previousPageButton.disabled = page <= 1;
    this.nextPageButton.disabled = page >= totalPages;
    this.pageStatus.textContent = `Page ${page} of ${totalPages}`;
    this.announce(`${items.length} products shown. Page ${page} of ${totalPages}.`);

    this.resultsSection.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  createProductCard(item) {
    const listItem = document.createElement('li');
    listItem.className = 'store-image-search__product';

    const url = this.safeHttpUrl(item.externalUrl);
    const link = document.createElement(url ? 'a' : 'div');
    link.className = 'store-image-search__product-link';
    if (url) link.href = url;
    if (!url) link.setAttribute('aria-disabled', 'true');

    const imageWrap = document.createElement('div');
    imageWrap.className = 'store-image-search__product-image-wrap';

    const imageUrl = this.safeHttpUrl(item.mainImageUrl);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'store-image-search__product-image';
      image.src = imageUrl;
      image.alt = item.productName ? `${item.productName} product image` : 'Product image';
      image.loading = 'lazy';
      image.width = 480;
      image.height = 480;
      imageWrap.append(image);
    }

    const score = Number(item.score);
    if (Number.isFinite(score)) {
      const match = document.createElement('span');
      match.className = 'store-image-search__match';
      match.textContent = `${Math.round(Math.max(0, Math.min(1, score)) * 100)}% visual match`;
      imageWrap.append(match);
    }

    const title = document.createElement('h4');
    title.className = 'store-image-search__product-title';
    title.textContent = item.productName || 'View product';

    const meta = document.createElement('p');
    meta.className = 'store-image-search__product-meta';
    const skuCount = Math.max(0, Number(item.skuCount) || 0);
    meta.textContent = url
      ? `${skuCount} ${skuCount === 1 ? 'variant' : 'variants'}`
      : 'Product page unavailable';

    link.append(imageWrap, title, meta);
    listItem.append(link);
    return listItem;
  }

  renderError(error) {
    const code = Number(error.code) || 500;
    const messages = {
      400: 'Please choose a valid image and try again.',
      401: 'Unable to verify this request. Please refresh and try again.',
      403: 'Image search is not available for this store.',
      404: 'Store connection was not found.',
      42900: 'Image search is busy. Please try again shortly.',
      500: "We couldn't complete the search. Please try again.",
    };

    this.errorMessage.textContent = messages[code] || messages[500];
    this.errorState.hidden = false;
    this.announce(this.errorMessage.textContent);

    if (code === 403) {
      document.dispatchEvent(new CustomEvent('store-image-search:unavailable'));
    }
  }

  openSourcing() {
    const sourcingRequest = this.root?.querySelector('store-sourcing-request');
    if (!sourcingRequest) return;

    if (this.file && typeof sourcingRequest.selectFiles === 'function') {
      sourcingRequest.selectFiles([this.file]);
    }

    const tabs = this.root.querySelector('search-mode-tabs');
    if (tabs && typeof tabs.setMode === 'function') {
      tabs.setMode('sourcing', true);
      return;
    }

    window.location.hash = 'Sourcing';
  }

  reset() {
    this.abortPendingRequest();
    this.revokePreviewUrl();
    this.file = null;
    this.currentPage = 1;
    this.fileInput.value = '';
    this.previewImage.removeAttribute('src');
    this.preview.hidden = true;
    this.dropzoneContent.hidden = false;
    this.searchButton.disabled = true;
    this.resetButton.hidden = true;
    this.clearFileError();
    this.clearResponseStates();
    this.fileInput.focus();
    this.announce('Image selection cleared.');
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.loadingState.hidden = !isLoading;
    this.loadingState.setAttribute('aria-hidden', String(!isLoading));
    this.searchButton.disabled = isLoading || !this.file;
    this.fileInput.disabled = isLoading;
    this.buttonSpinner.hidden = !isLoading;
    this.searchButtonLabel.textContent = isLoading ? 'Searching' : 'Find similar products';
    if (isLoading) this.announce('Searching for visually similar products.');
  }

  clearResponseStates() {
    this.loadingState.hidden = true;
    this.errorState.hidden = true;
    this.emptyState.hidden = true;
    this.resultsSection.hidden = true;
    this.pagination.hidden = true;
    this.resultsList.replaceChildren();
  }

  showFileError(message) {
    this.fileError.textContent = message;
    this.fileError.hidden = false;
    this.fileInput.setAttribute('aria-invalid', 'true');
    this.announce(message);
  }

  clearFileError() {
    this.fileError.textContent = '';
    this.fileError.hidden = true;
    this.fileInput.removeAttribute('aria-invalid');
  }

  abortPendingRequest() {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
    if (this.searchButton) this.setLoading(false);
    else this.isLoading = false;
  }

  revokePreviewUrl() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }

  safeHttpUrl(value) {
    if (!value || typeof value !== 'string') return null;
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
    } catch (_error) {
      return null;
    }
  }

  formatFileSize(bytes) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  announce(message) {
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 20);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('store-image-search')) {
  customElements.define('store-image-search', StoreImageSearch);
}
